// src/components/onboarding/OnboardingUI.js
import React from 'react';

export const ProgressBar = ({ current, total }) => {
  const progress = Math.min((current / total) * 100, 100);
  return (
    <div className="w-full min-h-1 bg-gray-700 rounded mb-6">
      <div
        className="h-full bg-gradient-to-r from-emerald-300 to-emerald-600 rounded transition-all duration-300"
        style={{ width: `${progress}%` }}
      />
    </div>
  );
};

export const CheckboxItem = ({ checked, label, onClick, disabled }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className={`w-full text-left p-4 rounded-xl border transition-all mb-2 flex items-center gap-3
      ${checked
        ? 'bg-emerald-500/10 border-emerald-600 text-gray-100'
        : 'bg-emerald-500/[0.03] border-white/10 text-gray-400 hover:border-emerald-600'
      }
      ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
  >
    <div className={`w-5 h-5 rounded flex-shrink-0 flex items-center justify-center border-2 transition-all
      ${checked ? 'bg-emerald-500 border-emerald-600' : 'border-gray-500'}`}>
      {checked && <span className="text-white text-xs">✓</span>}
    </div>
    <span className="flex-1">{label}</span>
  </button>
);

export const RadioItem = ({ checked, label, sublabel, onClick, disabled }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className={`w-full text-left p-4 rounded-xl border transition-all mb-2 flex items-center gap-3
      ${checked
        ? 'bg-emerald-500/10 border-emerald-600 text-gray-100'
        : 'bg-emerald-500/[0.03] border-white/10 text-gray-400 hover:border-emerald-600'
      }
      ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
  >
    <div className={`w-5 h-5 rounded-full flex-shrink-0 flex items-center justify-center border-2 transition-all
      ${checked ? 'border-emerald-500' : 'border-gray-500'}`}>
      {checked && <div className="w-2.5 h-2.5 rounded-full bg-emerald-500" />}
    </div>
    <div className="flex-1">
      <div className={`font-medium ${checked ? 'text-gray-100' : 'text-gray-300'}`}>{label}</div>
      {sublabel && <div className="text-sm text-gray-500 mt-0.5">{sublabel}</div>}
    </div>
  </button>
);

export const PrimaryButton = ({ onClick, disabled, loading, children }) => (
  <button
    onClick={onClick}
    disabled={disabled || loading}
    className={`w-full p-4 font-semibold transition-all flex items-center justify-center gap-2
      ${disabled || loading
        ? 'btn cursor-not-allowed'
        : 'btn'
      }`}
  >
    {loading && (
      <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
      </svg>
    )}
    {children}
  </button>
);

export const SectionBox = ({ variant = 'gray', icon, label, children }) => {
  const variants = {
    gray: { border: 'border-gray-500', bg: 'bg-gray-800/30', iconBg: 'bg-gray-700', labelColor: 'text-gray-400' },
    emerald: { border: 'border-emerald-500', bg: 'bg-gray-500/10', iconBg: 'bg-emerald-500/30', labelColor: 'text-emerald-400' },
    teal: { border: 'border-teal-500', bg: 'bg-teal-500/10', iconBg: 'bg-teal-500/30', labelColor: 'text-teal-400' },
    amber: { border: 'border-amber-500', bg: 'bg-amber-500/10', iconBg: 'bg-amber-500/30', labelColor: 'text-amber-400' }
  };
  const v = variants[variant] || variants.gray;

  return (
    <div className={`${v.bg} border-l-4 ${v.border} rounded-xl p-4 mb-4`}>
      <div className="flex items-center gap-2 mb-3">
        <div className={`w-8 h-8 rounded-lg ${v.iconBg} flex items-center justify-center text-sm`}>{icon}</div>
        <span className={`text-sm font-semibold ${v.labelColor}`}>{label}</span>
      </div>
      {children}
    </div>
  );
};

export const ScreenTitle = ({ title, subtitle }) => (
  <>
    <h2 className="text-xl font-bold text-gray-100 mb-2">{title}</h2>
    {subtitle && <p className="text-gray-400 mb-4">{subtitle}</p>}
  </>
);
